import {
  Currency,
  type FullSupplySignalDTO,
  Incoterms,
  type PricedDemandSignalDTO,
  useApiTradeV1ConvertPriceToMarketIndexPercentageRetrieve,
} from 'api';
import {
  IncotermSelectField,
  PortAutocomplete,
  PriceAndCurrencyField,
  TextField,
  TradeCompanyEmployeeAutocompleteField,
  useFormContext,
} from 'form';
import { useState } from 'react';
import {
  ActionRow,
  CircularProgress,
  formatNumber,
  formatPricePerTonne,
  Grid,
  IconButton,
  type SelectChangeEvent,
  Stack,
  Typography,
} from 'ui';
import { Refresh } from 'ui/icons';

import { useMatchedSiteMaterialItem } from '../../contexts/MatchedSiteMaterialItemContext';
import { useSellerOfferPrice } from '../../hooks/useSellerOfferPrice/useSellerOfferPrice';
import type { Recipient } from '../../types';
import { assembleHelperPriceText } from '../../utils/assembleHelperText/assembleHelperText';
import { formatMatchPrice } from '../../utils/formatMatchPrice/formatMatchPrice';
import { getPreferredPortOfLoadingIdFromMatch } from '../../utils/preferrredPortOfLoading/getPreferredPortOfLoadingIdFromMatch';
import type { DrawerFormValues } from '../MatchedSiteMaterialItemDrawer/MatchedSiteMaterialItemDrawer';
import { SellerTermsFormSender } from '../SellerTermsFormSender/SellerTermsFormSender';
import { ShareMatch } from '../ShareMatch/ShareMatch';
const INCOTERMS_WHEN_PORT_IS_REQUIRED: Incoterms[] = [Incoterms.FAS, Incoterms.FOB];

export type Props = {
  isTransportPriceLoading: boolean;
  estimateTransportPrice: () => void;
};

const SupplySignalDescription = ({ supplySignal }: { supplySignal?: FullSupplySignalDTO }) => {
  if (!supplySignal) {
    return <Typography>We don&apos;t have a supply signal for this site material</Typography>;
  }
  return (
    <Typography variant="body2">
      Live supply signal:{' '}
      <Typography variant="body2" fontWeight="bold" display="inline">
        {formatMatchPrice({
          currency: supplySignal.currency,
          priceAsIndexPercentage: supplySignal.price_as_index_percentage ?? undefined,
          priceIndex: supplySignal.price_index ?? undefined,
          pricePerTonne: supplySignal.price_per_tonne ?? undefined,
        })}
      </Typography>
    </Typography>
  );
};

export const SellerOfferDrawerSection = ({ isTransportPriceLoading, estimateTransportPrice }: Props) => {
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | undefined>(undefined);
  const {
    siteMaterial,
    supplySignal,
    demandSignal,
    targetMargin,
    initialSellerOffer: initialOffer,
  } = useMatchedSiteMaterialItem();
  const form = useFormContext<DrawerFormValues>();
  const { watch, setValue } = form;
  const pricePerTonne = watch('seller_price_per_tonne');
  const currency = watch('seller_currency');

  const { data: indexPercentage } = useApiTradeV1ConvertPriceToMarketIndexPercentageRetrieve({
    currency,
    material_type_id: siteMaterial.material_spec.material.material_type.id,
    price: pricePerTonne,
  });

  const onOfferPriceSuccess = (prices: PricedDemandSignalDTO[]) => {
    const priceDetails = prices[0].price_details;
    if (priceDetails) {
      setValue('seller_price_per_tonne', Math.ceil(Number(priceDetails.seller_price_per_tonne)));
      setValue('seller_currency', priceDetails.seller_currency);
    } else {
      setValue('seller_price_per_tonne', 0);
      setValue('seller_currency', Currency.USD);
    }
  };

  const {
    isPending: isPriceLoading,
    computePrice,
    priceError,
  } = useSellerOfferPrice({ onSuccess: onOfferPriceSuccess });

  const onCalculatePriceClick = async () => {
    const data = form.getValues();
    computePrice({
      demandSignalIds: [demandSignal.id],
      incoterm: data.supply_incoterm,
      portOfLoadingId: data.port_of_loading,
      siteMaterialId: siteMaterial.id,
      targetMargin,
      tonnes: (data.tonnes_per_load * data.number_of_loads).toString(),
      tonnesPerLoad: data.tonnes_per_load.toString(),
      transportCostPerLoad: data.transport_cost,
      transportCurrency: data.transport_currency,
    });
  };

  const isSendingDisabled = isPriceLoading || isTransportPriceLoading;

  const hasError = Boolean(priceError) || Boolean(initialOffer?.price_error);

  const helperPriceText = assembleHelperPriceText(priceError || initialOffer?.price_error, null);

  const handleIncotermChange = (event: SelectChangeEvent<string>) => {
    if (INCOTERMS_WHEN_PORT_IS_REQUIRED.includes(event.target.value as Incoterms)) {
      const newPortValue = getPreferredPortOfLoadingIdFromMatch(siteMaterial);
      setValue('port_of_loading', newPortValue);
    }
    estimateTransportPrice();
  };

  return (
    <Stack direction="column" gap={2} data-testid="seller-offer-section">
      <Typography variant="body2" fontWeight="bold">
        Seller offer
      </Typography>
      <SupplySignalDescription supplySignal={supplySignal} />
      <Grid container spacing={1}>
        <Grid item xs={4.5}>
          <Stack direction="row">
            <PriceAndCurrencyField
              label="Price per tonne"
              priceName="seller_price_per_tonne"
              currencyName="seller_currency"
              fullWidth
              slotProps={{
                currency: {
                  'data-testid': 'currency',
                },
                price: {
                  error: hasError,
                  helperText: helperPriceText,
                },
              }}
            />

            <div>
              <IconButton onClick={onCalculatePriceClick}>
                {isPriceLoading ? <CircularProgress size={24} /> : <Refresh fontSize="medium" />}
              </IconButton>
            </div>
          </Stack>
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            name="seller_price_index_percentage"
            label="% of LME"
            disabled
            value={formatNumber(indexPercentage ? indexPercentage.price_as_index_percentage : 0, {
              minimumFractionDigits: 0,
              style: 'percent',
            })}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <IncotermSelectField label="Incoterm" name="supply_incoterm" fullWidth onChange={handleIncotermChange} />
        </Grid>

        <Grid item xs={2.5}>
          <PortAutocomplete
            name="port_of_loading"
            label="Port of loading"
            fullWidth
            defaultValue={form.getValues('port_of_loading')}
            required={INCOTERMS_WHEN_PORT_IS_REQUIRED.includes(form.getValues('supply_incoterm'))}
            onSelectOption={(option) => {
              setValue('port_of_loading', option.id);
              estimateTransportPrice();
            }}
          />
        </Grid>

        <Grid item xs={2}>
          <TradeCompanyEmployeeAutocompleteField
            tradeCompanyId={siteMaterial.site.trade_company.id}
            name="seller_recipient"
            label="Recipient"
            required
            fullWidth
            onSelectOption={(option) => {
              setSelectedRecipient({
                email: option.email,
                id: option.id,
                phone_number: option.phone_number,
              });
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="right">
            <ActionRow>
              <ShareMatch
                matchedTradeCompanyId={siteMaterial.site.trade_company.id}
                siteMaterial={siteMaterial}
                demandSignalUrl={demandSignal.hubspot_url}
                supplySignalUrl={supplySignal?.hubspot_url}
                buyerName={demandSignal.buyer?.name || 'Not specified'}
                buyerCountry={demandSignal.buyer?.country ?? undefined}
                offerPrice={pricePerTonne ? formatPricePerTonne(pricePerTonne, currency) : undefined}
                incoterm={form.getValues('supply_incoterm')}
                port_id={form.getValues('port_of_loading')}
              />

              <SellerTermsFormSender
                disabled={isSendingDisabled}
                siteMaterialId={siteMaterial.id}
                selectedEmployee={selectedRecipient}
              />
            </ActionRow>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
